import { Stack, Typography } from '@mui/material';
import { ComponentType } from 'react';

import { AlertTriangleFilledIcon } from '@/components/icons/AlertTriangleFilledIcon';
import { IconProps } from '@/components/icons/ChevronDownIcon';
import { HouseholdDataIcon } from '@/components/icons/HouseholdDataIcon';
import { PlatformQuestionsIcon } from '@/components/icons/PlatformQuestionsIcon';
import { WaterfallIcon } from '@/components/icons/WaterfallIcon';
import { COLORS } from '@/styles/tokens/colors';

import { useHouseholdChatContext } from '../contexts/householdChat.context';
import { HouseholdChatSuggestion } from './HouseholdChatSuggestion';

interface SuggestedQuestion {
  question: string;
  // Leading icon, chosen to hint at the kind of question (overview, what-if
  // scenario, how-to guidance) so the set is easier to scan.
  Icon: ComponentType<IconProps>;
}

const SUGGESTED_QUESTIONS: SuggestedQuestion[] = [
  { question: 'Summarize this estate plan', Icon: HouseholdDataIcon },
  { question: 'What happens at first death?', Icon: WaterfallIcon },
  {
    question: 'How do I enter dispositions?',
    Icon: PlatformQuestionsIcon,
  },
];

// Match the suggestion text color (see ChatSuggestion) so each icon reads as
// part of its label rather than a separate accent.
const SUGGESTION_ICON_COLOR = COLORS.NAVY[900];

export function HouseholdChatEmptyState() {
  const { error } = useHouseholdChatContext();

  if (error) {
    return (
      <Stack alignItems="center" gap={1} px={3}>
        <AlertTriangleFilledIcon
          size={16}
          sx={{
            color: COLORS.FUNCTIONAL.ERROR[600],
          }}
        />
        <Typography
          variant="body-xs"
          sx={{
            color: COLORS.FUNCTIONAL.ERROR[600],
            fontWeight: 500,
            textAlign: 'center',
          }}
        >
          {error}
        </Typography>
      </Stack>
    );
  }

  return (
    <Stack alignItems="flex-start" gap={2} sx={{ width: '100%', px: 2 }}>
      <Typography variant="title-xs" sx={{ color: COLORS.TEXT.PRIMARY }}>
        What can I ask?
      </Typography>
      <Stack alignItems="flex-start" gap={1} sx={{ width: '100%' }}>
        {SUGGESTED_QUESTIONS.map(({ question, Icon }) => (
          <HouseholdChatSuggestion
            key={question}
            question={question}
            icon={
              <Icon
                size={16}
                aria-hidden
                sx={{ color: SUGGESTION_ICON_COLOR }}
              />
            }
          />
        ))}
      </Stack>
    </Stack>
  );
}
